import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ColumnVisibilityToggle from '../../../community/residents/components/ColumnVisibilityToggle.jsx';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';
import ActiveFilterBar from '../../../community/residents/components/ActiveFilterBar.jsx';
import { exportLightChannelsCsv } from '../../../../services/csvExport.js';
import usePersistedColumns from '../../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import NoneActionCell from '../../../../components/v2/NoneActionCell.jsx';

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------
const COLOR_PRESETS = {
    White: { r: 255, g: 255, b: 255 },
    Red: { r: 255, g: 0, b: 0 },
    Green: { r: 0, g: 255, b: 0 },
    Blue: { r: 0, g: 0, b: 255 },
};

function detectColorLabel(r, g, b) {
    for (const [name, preset] of Object.entries(COLOR_PRESETS)) {
        if (preset.r === r && preset.g === g && preset.b === b) return name;
    }
    return 'Custom';
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------
const COLUMN_DEFS = [
    { key: 'channel_id', label: 'Channel #', sortable: true, filterable: false, required: true },
    { key: 'channel_name', label: 'Name', sortable: true, filterable: false, required: true },
    { key: 'color_label', label: 'Color', sortable: true, filterable: true, required: true },
    { key: 'flashing_display', label: 'Flashing', sortable: true, filterable: true, required: false },
    { key: 'device_count', label: 'Devices', sortable: true, filterable: false, required: false },
];

// ---------------------------------------------------------------------------
// Devices popover cell — badge with hover list
//
// When `onEdit` + `row` are provided the badge click opens the Edit Channel
// drawer (same target as the row kebab); the hover preview keeps working.
// With no devices, "None" keeps the muted look but carries the same action so
// a first device can be assigned from here.
// ---------------------------------------------------------------------------
const DevicesPopoverCell = ({ deviceObjects, onEdit, row }) => {
    const [open, setOpen] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState({});
    const ref = useRef(null);
    const hoverTimeout = useRef(null);
    const isClickToEdit = typeof onEdit === 'function';

    if (!deviceObjects || deviceObjects.length === 0) {
        return (
            <NoneActionCell
                className="ap-cell-text--muted"
                onClick={isClickToEdit ? () => onEdit(row) : undefined}
                title="Click to edit channel"
            />
        );
    }

    const computePosition = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        if (spaceBelow < 200) {
            setPopoverStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, left: `${rect.left}px`, top: 'auto', right: 'auto', margin: 0, zIndex: 9999, transform: 'none' });
        } else {
            setPopoverStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, left: `${rect.left}px`, bottom: 'auto', right: 'auto', margin: 0, zIndex: 9999, transform: 'none' });
        }
    };

    const handleMouseEnter = () => { clearTimeout(hoverTimeout.current); computePosition(); setOpen(true); };
    const handleMouseLeave = () => { hoverTimeout.current = setTimeout(() => setOpen(false), 100); };

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    useEffect(() => () => clearTimeout(hoverTimeout.current), []);

    return (
        <div className="ap-device-cell" ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button
                type="button"
                className="ap-device-badge"
                onClick={() => {
                    if (isClickToEdit) {
                        setOpen(false);
                        onEdit(row);
                    } else {
                        computePosition();
                        setOpen(!open);
                    }
                }}
                title={isClickToEdit ? 'Click to edit channel' : undefined}
                aria-label={isClickToEdit
                    ? `Edit channel — ${deviceObjects.length} device${deviceObjects.length !== 1 ? 's' : ''} assigned`
                    : `${deviceObjects.length} device${deviceObjects.length !== 1 ? 's' : ''} assigned`}
            >
                <i className="fe fe-cpu" style={{ fontSize: '12px' }} />
                {deviceObjects.length}
            </button>
            {open ? (
                <div className="ap-device-popover" style={popoverStyle}>
                    <div className="ap-device-popover__title">Devices ({deviceObjects.length})</div>
                    <ul className="ap-device-popover__list">
                        {deviceObjects.map((d, i) => (
                            <li key={d.device_id || i} className="ap-device-popover__item">
                                <span className="ap-device-popover__name">{d.device_name || '—'}</span>
                                <span className="ap-device-popover__uuid">
                                    {[d.device_category, d.descriptions, d.manufacture_uuid, d.entity_name].filter(Boolean).join(' · ')}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Row-level kebab menu
// ---------------------------------------------------------------------------
const RowActions = ({ row, onEdit }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const handleToggle = () => {
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 120) {
                setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            } else {
                setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            }
        }
        setOpen(!open);
    };

    return (
        <div className="ap-row-actions" ref={menuRef}>
            <button type="button" onClick={handleToggle} className="ap-row-actions__trigger" aria-label={`Actions for channel ${row.channel_id}`} aria-expanded={open}>
                <i className="fe fe-more-vertical" aria-hidden="true" />
            </button>
            {open ? (
                <div className="ap-row-actions__menu" style={menuStyle}>
                    <button type="button" onClick={() => { onEdit(row); setOpen(false); }} className="ap-row-actions__item">
                        <i className="fe fe-edit ap-row-actions__item-icon" aria-hidden="true" />
                        Edit Channel
                    </button>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const ALL_COLUMN_KEYS = COLUMN_DEFS.map((c) => c.key);

const ChannelTable = ({ channels, controllerName, onEdit, saving }) => {
    const DEFAULT_VISIBLE = useMemo(() => COLUMN_DEFS.map((c) => c.key), []);
    const COLUMN_LABELS = useMemo(() => {
        const labels = {};
        COLUMN_DEFS.forEach((col) => { labels[col.key] = col.label; });
        return labels;
    }, []);

    const [search, setSearch] = useState('');
    const [sortColumn, setSortColumn] = useState('channel_id');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filters, setFilters] = useState({});
    const [visibleColumns, handleColumnToggle] = usePersistedColumns('settings-light-channels', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    // Reset state when channels change (tab switch)
    useEffect(() => {
        setSearch('');
        setFilters({});
        setSortColumn('channel_id');
        setSortOrder('asc');
        setPage(1);
    }, [channels]);

    // Enrich data
    const enrichedData = useMemo(() => {
        return (channels || []).map((ch) => {
            const r = ch.red_intensity ?? 255;
            const g = ch.green_intensity ?? 255;
            const b = ch.blue_intensity ?? 255;
            return {
                ...ch,
                color_label: detectColorLabel(r, g, b),
                color_rgb: `rgb(${r}, ${g}, ${b})`,
                flashing_display: ch.is_flashing === 1 ? 'Yes' : 'No',
                device_count: ch.devices?.length || 0,
                _deviceObjects: ch.devices || [],
            };
        });
    }, [channels]);

    // Active columns
    const activeColumns = useMemo(
        () => COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
        [visibleColumns]
    );

    // Filter values
    const getFilterValues = useCallback((columnKey) => {
        const values = new Set();
        enrichedData.forEach((row) => {
            const val = row[columnKey];
            values.add(val != null && val !== '' ? String(val) : '(empty)');
        });
        return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }, [enrichedData]);

    // ======================================================================
    // Data pipeline: Search → Filters → Sort
    // ======================================================================
    const filteredData = useMemo(() => {
        let d = [...enrichedData];

        if (search.trim()) {
            const term = search.toLowerCase();
            d = d.filter((r) =>
                String(r.channel_id).includes(term) ||
                (r.channel_name || '').toLowerCase().includes(term) ||
                (r.color_label || '').toLowerCase().includes(term)
            );
        }

        Object.entries(filters).forEach(([columnKey, values]) => {
            if (!values || values.length === 0) return;
            d = d.filter((row) => {
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return values.includes(strVal);
            });
        });

        d.sort((a, b) => {
            const aVal = a[sortColumn] ?? '';
            const bVal = b[sortColumn] ?? '';
            const result = typeof aVal === 'number' && typeof bVal === 'number'
                ? aVal - bVal
                : String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
            return sortOrder === 'asc' ? result : -result;
        });

        return d;
    }, [enrichedData, search, filters, sortColumn, sortOrder]);

    useEffect(() => { setPage(1); }, [search, filters, sortColumn, sortOrder]);

    // ======================================================================
    // Pagination
    // ======================================================================
    const totalFiltered = filteredData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * perPage;
    const endIdx = Math.min(startIdx + perPage, totalFiltered);
    const paginatedData = filteredData.slice(startIdx, endIdx);

    const showingStart = totalFiltered === 0 ? 0 : startIdx + 1;
    const showingEnd = endIdx;

    const pageNumbers = useMemo(() => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }, [safePage, totalPages]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((column) => {
        if (sortColumn === column) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    }, [sortColumn]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({ ...prev, [columnKey]: (prev[columnKey] || []).filter((v) => v !== value) }));
    }, []);

    const handleClearAllFilters = useCallback(() => { setFilters({}); setSearch(''); }, []);

    const handleExportAll = useCallback(() => {
        if (filteredData.length) exportLightChannelsCsv(filteredData, controllerName);
    }, [filteredData, controllerName]);

    // ======================================================================
    // Sort icons
    // ======================================================================
    const renderSortIcon = (columnKey) => {
        if (sortColumn !== columnKey) {
            return (
                <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
            );
        }
        return sortOrder === 'asc' ? (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
        ) : (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
        );
    };

    // ======================================================================
    // Custom cell rendering
    // ======================================================================
    const renderCell = (row, columnKey) => {
        switch (columnKey) {
            case 'color_label':
                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span className="lc-color-swatch" style={{ backgroundColor: row.color_rgb }} />
                        <span className="ap-cell-text">{row.color_label}</span>
                    </span>
                );
            case 'flashing_display': {
                const isOn = row.flashing_display === 'Yes';
                return (
                    <span className={`ws-status ${isOn ? 'ws-status--active' : 'ws-status--inactive'}`}>
                        <span className="ws-status__dot" aria-hidden="true" />
                        {row.flashing_display}
                    </span>
                );
            }
            case 'channel_name':
                return <span className="ap-cell-text">{row.channel_name || '\u2014'}</span>;
            case 'device_count':
                return <DevicesPopoverCell deviceObjects={row._deviceObjects} onEdit={onEdit} row={row} />;
            default:
                return <span className="ap-cell-text">{row[columnKey] != null ? row[columnKey] : '\u2014'}</span>;
        }
    };

    // ======================================================================
    // Render
    // ======================================================================
    const hasFilters = search.trim() || Object.values(filters).some((v) => v.length > 0);

    return (
        <>
            {/* Controls row */}
            <div className="ap-controls">
                <div className="ap-controls__left">
                    <div className="ap-search">
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input type="search" placeholder="Search channels..." value={search} onChange={(e) => setSearch(e.target.value)} className="ap-search__input" aria-label="Search channels" />
                    </div>
                </div>
                <div className="ap-controls__right">
                    <ColumnVisibilityToggle columns={COLUMN_DEFS} visibleColumns={visibleColumns} onToggle={handleColumnToggle} />
                    <button type="button" className="ap-btn ap-btn--secondary" onClick={handleExportAll} aria-label="Export channels">
                        <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                        </svg>
                        Export
                    </button>
                </div>
            </div>

            {/* Active filter chips */}
            <ActiveFilterBar filters={filters} columnLabels={COLUMN_LABELS} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />

            {/* Table */}
            {filteredData.length === 0 ? (
                <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
                    <p className="ss-empty__text">
                        {hasFilters ? 'No channels match your filters.' : 'No channels configured.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="ap-table-wrap">
                        <table className="ap-table">
                            <thead className="ap-thead">
                                <tr>
                                    <th scope="col" className="ap-th ap-th--actions">Edit</th>
                                    {activeColumns.map((col) => (
                                        <th key={col.key} scope="col" className="ap-th">
                                            <div className="ap-th__content">
                                                {col.sortable ? (
                                                    <button type="button" onClick={() => handleSort(col.key)} className="ap-th__sort-btn" aria-label={`Sort by ${col.label}`}>
                                                        {col.label}
                                                        {renderSortIcon(col.key)}
                                                    </button>
                                                ) : <span>{col.label}</span>}
                                                {col.filterable ? (
                                                    <FilterPopover columnKey={col.key} label={col.label} values={getFilterValues(col.key)} selectedValues={filters[col.key] || []} onChange={handleFilterChange} />
                                                ) : null}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="ap-tbody">
                                {paginatedData.map((row) => (
                                    <tr key={row.light_relay_id} className="ap-tr">
                                        <td className="ap-td ap-td--actions">
                                            <RowActions row={row} onEdit={onEdit} />
                                        </td>
                                        {activeColumns.map((col) => (
                                            <td key={col.key} className="ap-td">
                                                {renderCell(row, col.key)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="ap-pagination">
                        <p className="ap-pagination__info">
                            Showing <span className="ap-pagination__emphasis">{showingStart}</span>&ndash;<span className="ap-pagination__emphasis">{showingEnd}</span> of{' '}
                            <span className="ap-pagination__emphasis">{totalFiltered}</span> channel{totalFiltered !== 1 ? 's' : ''}
                        </p>
                        <div className="ap-pagination__controls">
                            <div className="ap-per-page">
                                <label htmlFor="lc-per-page" className="ap-per-page__label">Rows per page:</label>
                                <select id="lc-per-page" value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); }} className="ap-per-page__select">
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                    <option value={300}>300</option>
                                </select>
                            </div>
                            <nav className="ap-page-nav" aria-label="Channel pagination">
                                <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="ap-page-btn" aria-label="Previous page">
                                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                                    </svg>
                                </button>
                                {pageNumbers.map((num) => (
                                    <button key={num} type="button" onClick={() => setPage(num)} className={`ap-page-num${num === safePage ? ' is-active' : ''}`} aria-label={`Page ${num}`} aria-current={num === safePage ? 'page' : undefined}>
                                        {num}
                                    </button>
                                ))}
                                <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="ap-page-btn" aria-label="Next page">
                                    <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default ChannelTable;
